/**
 * Native session backup and biometric unlock (session re-entry, phase 2).
 *
 * In the Capacitor shell the whole Supabase session lives in WebView cookies.
 * If the OS purges them (low memory, WebView data reset, an interrupted cookie
 * flush) the person lands on /login although the server session is alive for
 * up to a year. This module keeps a copy of the REFRESH token in the platform
 * secure store (iOS Keychain, Android Keystore-encrypted prefs) so the app can
 * rebuild the session silently at launch.
 *
 * Contract:
 *  - The backup is per device: `afterFirstUnlockThisDeviceOnly`, iCloud sync
 *    off, and the Android prefs file is excluded from cloud backup
 *    (res/xml/backup_rules.xml, data_extraction_rules.xml).
 *  - It is deleted on an explicit sign-out (`performSignOut`) and whenever the
 *    server says the token is dead (`refresh_token_not_found`, `invalid_grant`,
 *    `refresh_token_already_used`).
 *  - It belongs to one account: the user id is stored next to the token. A
 *    different account signing in overwrites it, and a restore that returns a
 *    different user is discarded.
 *  - The biometric flag ("Entrar con Face ID la próxima vez") is an app-level
 *    gate stored per user next to the backup. The OS does not bind the token to
 *    the biometric (the plugin has no access-control option): the token is
 *    protected by the Keychain/Keystore, the biometric decides whether the app
 *    uses it. Say so if someone asks "is it encrypted with my face".
 *  - Nothing here logs or reports a token. Errors carry codes only.
 *
 * Web: every function is a no-op that resolves to a neutral value.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import { isNativePlatform } from '@/lib/utils/platform'

/** Plugin key prefix: keeps our items apart from any other plugin's. */
const KEY_PREFIX = 'net.alumnia.session.'
const BACKUP_KEY = 'backup.v1'
const BIOMETRIC_KEY = 'biometric.v1'

export interface SessionBackup {
  refreshToken: string
  userId: string
}

type StoredBackup = SessionBackup & { v: 1; savedAt: number }

export type BiometricState = 'on' | 'declined'
type StoredBiometric = { v: 1; userId: string; state: BiometricState }

export type BiometricKind = 'faceId' | 'touchId' | 'fingerprint' | 'face' | 'iris' | 'generic'

export interface BiometricAvailability {
  available: boolean
  kind: BiometricKind
}

export type BiometricResult = 'success' | 'cancelled' | 'failed' | 'unavailable'

export type RestoreResult =
  | 'restored'
  | 'no-backup'
  | 'revoked'
  | 'user-mismatch'
  | 'transient-error'

/** Auth error codes (and legacy messages) that mean "this refresh token is dead". */
const DEAD_TOKEN_CODES = new Set([
  'refresh_token_not_found',
  'refresh_token_already_used',
  'invalid_grant',
  'session_not_found',
  'session_expired',
  'user_not_found',
  'user_banned',
])
const DEAD_TOKEN_MESSAGE =
  /invalid refresh token|refresh token not found|already used|invalid_grant/i

/** True when the auth error says the token is permanently dead (not a network blip). */
export function isDeadTokenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { code?: unknown; message?: unknown; name?: unknown; status?: unknown }
  if (e.name === 'AuthRetryableFetchError') return false
  if (typeof e.code === 'string' && DEAD_TOKEN_CODES.has(e.code)) return true
  if (typeof e.message === 'string' && DEAD_TOKEN_MESSAGE.test(e.message)) return true
  return false
}

// ── Secure storage plumbing ──────────────────────────────────────────────

type SecureStorageModule = typeof import('@aparajita/capacitor-secure-storage')

type SecureStoragePlugin = SecureStorageModule['SecureStorage']

/**
 * The Capacitor plugin is a Proxy that answers EVERY property read with a
 * native method stub, `then` included. Returning it from an async function (or
 * resolving a promise with it) makes JS read `.then`, and the proxy rejects
 * with `"SecureStorage.then()" is not implemented`. So the plugin is never the
 * resolved value of a promise: it travels inside this wrapper object.
 */
interface StorageHandle {
  plugin: SecureStoragePlugin
}

let storagePromise: Promise<StorageHandle | null> | null = null
let keychainAccess: SecureStorageModule['KeychainAccess'] | null = null

async function getStorage(): Promise<StorageHandle | null> {
  if (!isNativePlatform()) return null
  storagePromise ??= (async () => {
    try {
      const mod = await import('@aparajita/capacitor-secure-storage')
      keychainAccess = mod.KeychainAccess
      await mod.SecureStorage.setKeyPrefix(KEY_PREFIX)
      // Per device: never replicate through iCloud Keychain.
      await mod.SecureStorage.setSynchronize(false)
      return { plugin: mod.SecureStorage }
    } catch {
      // Plugin missing (older binary) or prefix call failed: degrade to no backup.
      return null
    }
  })()
  return storagePromise
}

async function writeJson(key: string, value: unknown): Promise<boolean> {
  const handle = await getStorage()
  if (!handle) return false
  try {
    await handle.plugin.set(
      key,
      JSON.stringify(value),
      false,
      false,
      keychainAccess?.afterFirstUnlockThisDeviceOnly
    )
    return true
  } catch {
    return false
  }
}

async function readJson<T>(key: string): Promise<T | null> {
  const handle = await getStorage()
  if (!handle) return null
  try {
    const raw = await handle.plugin.get(key, false, false)
    if (typeof raw !== 'string') return null
    return JSON.parse(raw) as T
  } catch {
    // Corrupted or undecryptable item (restored device, Keystore reset): drop it.
    try {
      await handle.plugin.remove(key, false)
    } catch {
      // nothing else to do
    }
    return null
  }
}

async function removeKey(key: string): Promise<void> {
  const handle = await getStorage()
  if (!handle) return
  try {
    await handle.plugin.remove(key, false)
  } catch {
    // A missing item is fine.
  }
}

// ── Backup ───────────────────────────────────────────────────────────────

/** Last token written in this launch: skips redundant keychain writes. */
let lastSavedToken: string | null = null
/** Set by an explicit sign-out; the page reloads to /login right after. */
let resyncBlocked = false

export async function readSessionBackup(): Promise<SessionBackup | null> {
  const stored = await readJson<StoredBackup>(BACKUP_KEY)
  if (
    !stored ||
    stored.v !== 1 ||
    typeof stored.refreshToken !== 'string' ||
    !stored.refreshToken ||
    typeof stored.userId !== 'string' ||
    !stored.userId
  ) {
    return null
  }
  return { refreshToken: stored.refreshToken, userId: stored.userId }
}

async function writeSessionBackup(backup: SessionBackup): Promise<void> {
  const ok = await writeJson(BACKUP_KEY, { v: 1, ...backup, savedAt: Date.now() })
  if (ok) lastSavedToken = backup.refreshToken
}

/**
 * Forget everything this module keeps: the token and the biometric choice.
 * Call on explicit sign-out. Never throws.
 */
export async function clearSessionBackup(opts: { blockResync?: boolean } = {}): Promise<void> {
  // An explicit sign-out blocks re-syncing for the rest of this document, so a
  // token refresh racing the sign-out cannot write the backup back.
  if (opts.blockResync) resyncBlocked = true
  lastSavedToken = null
  await Promise.all([removeKey(BACKUP_KEY), removeKey(BIOMETRIC_KEY)])
}

/**
 * Copy the live session's refresh token into the secure store (if it changed).
 * Returns the signed-in user id, or null when the WebView has no session.
 * A different account than the stored one replaces the backup and drops the
 * previous account's biometric choice.
 */
export async function syncBackupFromSession(
  supabase: Pick<SupabaseClient, 'auth'>
): Promise<string | null> {
  if (!isNativePlatform() || resyncBlocked) return null
  let session
  try {
    const { data } = await supabase.auth.getSession()
    session = data.session
  } catch {
    return null
  }
  const refreshToken = session?.refresh_token
  const userId = session?.user?.id
  if (!refreshToken || !userId) return null
  if (refreshToken === lastSavedToken) return userId

  const previous = await readSessionBackup()
  if (previous && previous.userId !== userId) {
    // Account switch on this device: the old account's unlock choice does not carry over.
    await removeKey(BIOMETRIC_KEY)
  }
  if (!previous || previous.refreshToken !== refreshToken || previous.userId !== userId) {
    await writeSessionBackup({ refreshToken, userId })
  } else {
    lastSavedToken = refreshToken
  }
  return userId
}

/**
 * Rebuild the session from the backup. Only call when the WebView has no
 * session. Maps the outcome so callers know whether to keep the backup:
 * dead tokens and account mismatches are deleted here; network failures keep it.
 */
export async function restoreSessionFromBackup(
  supabase: Pick<SupabaseClient, 'auth'>,
  backup?: SessionBackup | null
): Promise<RestoreResult> {
  const stored = backup ?? (await readSessionBackup())
  if (!stored) return 'no-backup'

  let result
  try {
    result = await supabase.auth.refreshSession({ refresh_token: stored.refreshToken })
  } catch {
    return 'transient-error'
  }
  const { data, error } = result
  if (error) {
    if (isDeadTokenError(error)) {
      await clearSessionBackup()
      return 'revoked'
    }
    return 'transient-error'
  }
  if (!data.session || data.user?.id !== stored.userId) {
    // Defensive: never keep a session that is not the account we backed up.
    try {
      await supabase.auth.signOut({ scope: 'local' })
    } catch {
      // best-effort
    }
    await clearSessionBackup()
    return 'user-mismatch'
  }
  // The refresh rotated the token: keep the new one.
  await writeSessionBackup({ refreshToken: data.session.refresh_token, userId: stored.userId })
  return 'restored'
}

// ── Biometric unlock ─────────────────────────────────────────────────────

type BiometricModule = typeof import('@aparajita/capacitor-biometric-auth')

async function getBiometric(): Promise<BiometricModule | null> {
  if (!isNativePlatform()) return null
  try {
    return await import('@aparajita/capacitor-biometric-auth')
  } catch {
    return null
  }
}

export async function getBiometricAvailability(): Promise<BiometricAvailability> {
  const mod = await getBiometric()
  if (!mod) return { available: false, kind: 'generic' }
  try {
    const info = await mod.BiometricAuth.checkBiometry()
    const { BiometryType } = mod
    const kinds: Record<number, BiometricKind> = {
      [BiometryType.faceId]: 'faceId',
      [BiometryType.touchId]: 'touchId',
      [BiometryType.fingerprintAuthentication]: 'fingerprint',
      [BiometryType.faceAuthentication]: 'face',
      [BiometryType.irisAuthentication]: 'iris',
    }
    return { available: info.isAvailable, kind: kinds[info.biometryType] ?? 'generic' }
  } catch {
    return { available: false, kind: 'generic' }
  }
}

/** Ask the OS for Face ID / fingerprint (device passcode allowed as the OS fallback). */
export async function authenticateWithBiometric(reason: string): Promise<BiometricResult> {
  const mod = await getBiometric()
  if (!mod) return 'unavailable'
  try {
    await mod.BiometricAuth.authenticate({
      reason,
      allowDeviceCredential: true,
      androidTitle: reason,
    })
    return 'success'
  } catch (error) {
    const code = (error as { code?: unknown } | null)?.code
    if (
      code === mod.BiometryErrorType.userCancel ||
      code === mod.BiometryErrorType.systemCancel ||
      code === mod.BiometryErrorType.appCancel
    ) {
      return 'cancelled'
    }
    if (
      code === mod.BiometryErrorType.biometryNotAvailable ||
      code === mod.BiometryErrorType.biometryNotEnrolled ||
      code === mod.BiometryErrorType.noDeviceCredential ||
      code === mod.BiometryErrorType.passcodeNotSet
    ) {
      return 'unavailable'
    }
    return 'failed'
  }
}

/** The stored choice for this user, or null when none was made (or it is another account's). */
export async function getBiometricState(userId: string): Promise<BiometricState | null> {
  const stored = await readJson<StoredBiometric>(BIOMETRIC_KEY)
  if (!stored || stored.v !== 1 || stored.userId !== userId) return null
  return stored.state === 'on' || stored.state === 'declined' ? stored.state : null
}

export async function setBiometricState(userId: string, state: BiometricState): Promise<void> {
  await writeJson(BIOMETRIC_KEY, { v: 1, userId, state } satisfies StoredBiometric)
}

/** Turn the unlock off (kept as "declined" so it is not offered again unasked). */
export async function disableBiometric(userId: string): Promise<void> {
  await setBiometricState(userId, 'declined')
}

/** Test seam: forget module-level caches. */
export function __resetSessionBackupForTests(): void {
  resyncBlocked = false
  storagePromise = null
  keychainAccess = null
  lastSavedToken = null
}
